import { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import { getUserInfoApiV1UsersMeGet } from "@/api/generated/sdk.gen";
import type { UserInfoResponseSchema } from "@/api/generated/types.gen";

import { CLIENT_KEYS } from "@constants/clientKeys";

/**
 * Fetch the current signed-in user (DB record incl. is_admin / user_type),
 * for both internal (Azure AD) and external (email-code) users. Used to gate
 * admin-only UI.
 */
export const useMe = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.ME],
    queryFn: async (): Promise<UserInfoResponseSchema> => {
      const response = await getUserInfoApiV1UsersMeGet({ client: apiClient });
      if ("error" in response && response.error) {
        throw new Error(
          `Failed to fetch user: ${
            (response.error as { detail?: string })?.detail ||
            JSON.stringify(response.error)
          }`
        );
      }
      if (!response.data) {
        throw new Error("User info not found");
      }
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

/** Convenience: is the current user an internal admin? */
export const useIsAdmin = (): boolean => {
  const { data } = useMe();
  return Boolean(data?.user?.is_admin);
};

/**
 * Is the current user looking at the stripped, doctor-facing view? (MDRS-325)
 *
 * True only for a non-admin `expert` — the doctor a case is assigned to. Their
 * Case page drops the internal service configuration (service type, case type,
 * report template) and the assigned-doctor line, keeping the free-text case type
 * as the description of what the case is about.
 *
 * Deliberately narrower than "not an admin": a non-admin `internal` staffer does
 * the back-office prep and keeps every field. Mirrors the backend gate in
 * `core/authz.may_edit_service_fields`, which is where it's actually enforced —
 * this only decides what gets rendered.
 */
export const useIsDoctorView = (): boolean => {
  const { data } = useMe();
  // Fails closed while `/me` is in flight: the gated fields stay hidden until
  // the viewer is confirmed NOT to be the assigned doctor, the same direction as
  // `can()` below. Resolving to `false` here would let a hard refresh straight
  // onto the Case page paint the template and assignee for a beat.
  if (!data?.user) return true;
  if (data.user.is_admin) return false;
  return (data.user.user_type ?? "").trim().toLowerCase() === "expert";
};

/**
 * The current user's derived capability set (access levels v1). UI gating only —
 * the backend re-checks per route. `can(cap)` is undefined-safe (returns false
 * while `/me` is loading), so gated UI stays hidden until confirmed allowed.
 */
export const useCapabilities = () => {
  const { data, isLoading } = useMe();
  // `can` and `capabilities` MUST be referentially stable.
  //
  // Both were rebuilt on every render, and several pages list `can` in an
  // effect's dependency array — `ReportBuilder` and `DocumentSorter` both run
  // an effect that calls `setRightContent(<HeaderActions …/>)` and clears it on
  // cleanup. A new `can` each render re-ran that effect, which set state in an
  // ANCESTOR, which re-rendered the page, which produced a new `can` … React
  // cut it off with "Maximum update depth exceeded" (MDRS-513).
  //
  // `capabilities` is memoised on the array identity `useMe` returns, so it
  // only changes when `/me` actually returns a different payload — note
  // `data?.capabilities ?? []` would otherwise mint a fresh [] on every render
  // while the query is in flight.
  const capabilities = useMemo(() => data?.capabilities ?? [], [data?.capabilities]);
  const can = useCallback(
    (capability: string): boolean => capabilities.includes(capability),
    [capabilities]
  );
  return { capabilities, isLoading, can };
};
